/**
 * What GET .../spaces/{id} puts in its `telemetry` field, and what a
 * "TelemetryUpdate" hub push carries -- shape depends entirely on the
 * caller's own resolved visibility level (see backend's
 * ITelemetryVisibilityPolicy), with no separate discriminator field on the
 * wire: use isPublicMachineryTelemetry (or the inverse) to tell them apart,
 * per the confirmed approach of keying off whether `status` is present
 * rather than asking the backend for an extra field.
 */
export interface IPublicMachineryTelemetry {
  /** Available | Rented | Maintenance. */
  status: 'Available' | 'Rented' | 'Maintenance';
}

/**
 * What a Host/ActiveCustomer/Admin-level caller gets -- identical shape for
 * all three (see FullMachineryTelemetryResponse's own doc comment on the
 * backend), only ever pushed to someone already authorized to see it.
 * Every field but machineryId is nullable: a machine that's never reported
 * telemetry yet (no TelemetryProviderConfig, or no reading has arrived)
 * still resolves to this shape once the caller is Host/ActiveCustomer/Admin,
 * just with everything but machineryId null.
 */
export interface IFullMachineryTelemetry {
  machineryId: string;
  latitude?: number;
  longitude?: number;
  engineHours?: number;
  fuelPercent?: number;
  ignitionOn?: boolean;
  lastUpdateUtc?: string;
}

export type IMachineryTelemetry = IPublicMachineryTelemetry | IFullMachineryTelemetry;

export const isPublicMachineryTelemetry = (telemetry: IMachineryTelemetry): telemetry is IPublicMachineryTelemetry =>
  'status' in telemetry;
